// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { TruncatedText } from '@/components/truncated-text';
import {
  CloudUploadOutlined,
  DeleteRounded,
  InsertDriveFileOutlined,
} from '@/constants/hardfin-standard-icons';
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { DragEvent, useRef, useState } from 'react';

/**
 * Represents a file being uploaded or already uploaded.
 */
export interface UploadingFile {
  /** Unique local identifier for tracking the file before upload completes */
  localId: string;
  /** The native File object */
  file: File;
  /** Current status of the upload */
  status: 'uploading' | 'complete' | 'error';
  /** Upload progress percentage (0-100) */
  progress?: number;
  /** Server-assigned file key after successful upload */
  serverKey?: string;
  /** Server-assigned file ID after successful upload */
  serverFileId?: string;
  /** Error message if upload failed */
  error?: string;
}

interface FileDropZoneProps {
  /** The list of files currently being uploaded or already uploaded */
  files: UploadingFile[];
  /** Callback fired when files are selected via drop or file picker */
  onFilesSelected: (files: File[]) => void;
  /** Callback fired when a file should be removed */
  onRemoveFile: (localId: string) => void;
  /** Whether the drop zone is disabled */
  disabled?: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * A drag-and-drop file upload zone styled like an input field.
 * Files appear inside the container with drag & drop text at the bottom.
 */
export function FileDropZone(props: FileDropZoneProps) {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!props.disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);

    if (props.disabled) {
      return;
    }

    const droppedFiles = Array.from(event.dataTransfer.files);
    if (droppedFiles.length > 0) {
      props.onFilesSelected(droppedFiles);
    }
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = event.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      props.onFilesSelected(Array.from(selectedFiles));
    }
    // Reset the input so the same file can be selected again
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  return (
    <Box
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleUploadClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        cursor: props.disabled ? 'default' : 'pointer',
        backgroundColor: isDragOver
          ? theme.palette.action.hover
          : 'transparent',
        border: '1px solid',
        borderColor: isDragOver
          ? theme.palette.primary.main
          : theme.palette.grey[300],
        borderRadius: 1,
        padding: 1.5,
        transition: 'all 0.2s ease',
        height: '100%',
        width: '100%',
        '&:hover': props.disabled
          ? {}
          : {
              borderColor: theme.palette.text.primary,
            },
      }}
    >
      {/* File list - compact rows */}
      {props.files.length > 0 && (
        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0, marginBottom: 1 }}>
          {props.files.map((uploadingFile) => (
            <Stack
              key={uploadingFile.localId}
              direction="row"
              alignItems="center"
              sx={{ minWidth: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* File name column - 85% */}
              <Box
                sx={{
                  width: '85%',
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {uploadingFile.status === 'uploading' ? (
                  <CircularProgress size={16} sx={{ flexShrink: 0 }} />
                ) : (
                  <InsertDriveFileOutlined
                    fontSize="small"
                    sx={{
                      color:
                        uploadingFile.status === 'error'
                          ? 'error.main'
                          : 'text.secondary',
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  />
                )}
                <TruncatedText
                  text={uploadingFile.file.name}
                  variant="body2"
                  color={
                    uploadingFile.status === 'error'
                      ? 'error.main'
                      : 'text.primary'
                  }
                />
              </Box>
              {/* File size + delete button - 15% */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                spacing={0.5}
                sx={{ width: '15%', flexShrink: 0 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {formatFileSize(uploadingFile.file.size)}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onRemoveFile(uploadingFile.localId);
                  }}
                  disabled={props.disabled}
                  sx={{ padding: 0.25 }}
                >
                  <DeleteRounded fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}

      {/* Drag & drop text at bottom */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={0.5}
        sx={{ marginTop: 'auto' }}
      >
        <CloudUploadOutlined
          sx={{
            fontSize: 18,
            color: isDragOver ? 'primary.main' : 'action.active',
          }}
        />
        <Typography
          variant="body2"
          color={isDragOver ? 'primary.main' : 'text.secondary'}
        >
          {isDragOver
            ? 'Drop files here'
            : 'Drag and drop files or click to upload'}
        </Typography>
      </Stack>

      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        multiple
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        disabled={props.disabled}
      />
    </Box>
  );
}
